import { formatResidentNames } from "@boark/shared/utils/apartment-residency";
import { useMemo } from "react";

import { useGetOrganizations } from "../queries/organization";
import { useGetPersons } from "../queries/person";
import { useLocale } from "../stores/locale";
import { Checkbox } from "./checkbox";
import { Input } from "./input";

export function ResidencyNameInput({
  residents,
  useCustomName,
  customName,
  disabled = false,
  className,
  onChange,
}: {
  residents: ({ personId: string } | { organizationId: string })[];
  useCustomName: boolean;
  customName: string;
  disabled?: boolean;
  className?: string;
  onChange: (values: { useCustomName: boolean; customName: string }) => void;
}) {
  const t = useLocale((state) => state.locale);
  const { data: persons } = useGetPersons();
  const { data: organizations } = useGetOrganizations();

  const defaultName = useMemo(
    () =>
      formatResidentNames(
        residents.map((resident) =>
          "personId" in resident
            ? { person: persons?.find(({ id }) => id === resident.personId) }
            : {
                organization: organizations?.find(
                  ({ id }) => id === resident.organizationId,
                ),
              },
        ),
      ),
    [residents, persons, organizations],
  );

  return (
    <div className={`flex flex-col gap-2 ${className ?? ""}`}>
      <Checkbox
        checked={useCustomName}
        className="flex items-center gap-2"
        disabled={disabled}
        label={t.useCustomName}
        onClick={() =>
          onChange({
            useCustomName: !useCustomName,
            customName:
              !useCustomName && customName.trim() === ""
                ? defaultName
                : customName,
          })
        }
      />
      <Input
        disabled={disabled || !useCustomName}
        label={t.customName}
        type="text"
        value={useCustomName ? customName : defaultName}
        onChange={(e) =>
          onChange({ useCustomName, customName: e.target.value })
        }
      />
    </div>
  );
}

export function residencyNameToFormValues(customName?: string): {
  useCustomName: boolean;
  customName: string;
} {
  return { useCustomName: !!customName, customName: customName ?? "" };
}

export function formValuesToResidencyName({
  useCustomName,
  customName,
}: {
  useCustomName: boolean;
  customName: string;
}): string | null {
  return useCustomName && customName.trim() !== "" ? customName.trim() : null;
}
